import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import api, { apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { useI18n } from "../lib/i18n";

export default function RequestItemDialog({ trip, open, onOpenChange }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    product_name: "", product_url: "", description: "", size: "", quantity: 1,
    budget: "", special_instructions: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_name || !form.budget) return toast.error("Product name & budget are required");
    setBusy(true);
    try {
      const { data } = await api.post("/requests", {
        type: "direct", bawainer_id: trip.bawainer.id, trip_id: trip.id,
        destination: trip.to_city, product_name: form.product_name,
        product_url: form.product_url, description: form.description, size: form.size,
        quantity: Number(form.quantity) || 1, budget: Number(form.budget),
        special_instructions: form.special_instructions,
      });
      toast.success("Request sent to " + trip.bawainer.name);
      onOpenChange(false);
      if (data.conversation_id) navigate(`/chat/${data.conversation_id}`);
      else navigate("/requests");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="request-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">
            {t("request_item")} · {trip?.to_city}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-sm font-semibold text-ink">Product name</label>
            <Input data-testid="req-product-name" value={form.product_name} onChange={set("product_name")}
              className="rounded-xl mt-1" placeholder="e.g. Nike Air Force 1" />
          </div>
          <div>
            <label className="text-sm font-semibold text-ink">Product URL (optional)</label>
            <Input data-testid="req-product-url" value={form.product_url} onChange={set("product_url")}
              className="rounded-xl mt-1" placeholder="https://..." />
          </div>
          <div>
            <label className="text-sm font-semibold text-ink">Description</label>
            <textarea data-testid="req-description" value={form.description} onChange={set("description")}
              className="w-full rounded-xl border border-input px-3 py-2 text-sm mt-1" rows={2}
              placeholder="Color, model, details..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-ink">Size / variant</label>
              <Input data-testid="req-size" value={form.size} onChange={set("size")} className="rounded-xl mt-1" placeholder="42 / M" />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Quantity</label>
              <Input data-testid="req-qty" type="number" min="1" value={form.quantity} onChange={set("quantity")} className="rounded-xl mt-1" />
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-ink">Max budget (Rp)</label>
            <Input data-testid="req-budget" type="number" value={form.budget} onChange={set("budget")}
              className="rounded-xl mt-1" placeholder="2500000" />
            {form.budget ? <p className="text-xs text-muted-foreground mt-1">{rupiah(form.budget)}</p> : null}
          </div>
          <div>
            <label className="text-sm font-semibold text-ink">Special instructions</label>
            <Input data-testid="req-instructions" value={form.special_instructions} onChange={set("special_instructions")}
              className="rounded-xl mt-1" placeholder="Optional" />
          </div>
          <div className="flex gap-2 items-start bg-orange-50 rounded-xl p-3 text-xs text-orange-700">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            Please make sure the item is legal to transport and permitted by the destination and origin country's regulations.
          </div>
          <button type="submit" disabled={busy} data-testid="req-submit"
            className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 transition-colors disabled:opacity-60">
            {busy ? "Sending..." : t("send_request")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
